import { Briefcase, Calendar, CheckCircle, Package } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAdminAuth } from "../../../context/AdminAuthContext";
import StaffAttendanceCalendar from "../../../components/StaffSideComponents/Dashboard/StaffAttendanceCalendar";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-xl border p-4 flex items-center gap-4 shadow-sm">
    <div className="w-10 h-10 rounded-full bg-primaryColor/10 flex items-center justify-center">
      <Icon size={18} className="text-primaryColor" />
    </div>
    <div>
      <p className="text-xs text-secondaryTextColor">{label}</p>
      <p className="text-lg font-semibold text-primaryTextColor">{value}</p>
    </div>
  </div>
);

const JobCard = ({ title, slot, location }) => (
  <div className="bg-white rounded-xl border p-4 shadow-sm space-y-1">
    <h3 className="font-semibold text-primaryTextColor">{title}</h3>
    <p className="text-xs text-secondaryTextColor">{location}</p>
    <p className="text-xs text-secondaryTextColor">{slot}</p>
  </div>
);

const StaffDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { admin } = useAdminAuth();
  const [workingHours, setWorkingHours] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/staff/${admin?.id}`
      );
      // console.log("data:", res?.data);
      setData(res.data?.data);
    } catch (err) {
      console.error("Failed to load staff dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchHours = async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/staff-assignment/working-hours/${admin?.id}`
      );
      setWorkingHours(res.data.data || []);
    };

    if (admin?.id) fetchHours();
  }, [admin?.id]);


  useEffect(() => {
    fetchDashboard();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-primaryColor/10 backdrop-blur border-b border-primaryColor/10 px-4 py-3">
          <h1 className="text-lg font-semibold text-primaryTextColor">
            Dashboard
          </h1>
          <p className="text-xs text-secondaryTextColor">
            Your work summary for today
          </p>
        </div>

        <StaffDashboardSkeleton />
      </div>
    );
  }

  const { assignments, schedule, products, actions } = data;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-primaryColor/10 to-accentTextColor/10 backdrop-blur-md border-b border-primaryColor/20 shadow-sm">
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            {/* Left */}
            <div className="leading-tight">
              <h1 className="text-base md:text-xl font-bold text-primaryTextColor">
                Dashboard
              </h1>
              <p className="text-xs md:text-sm text-secondaryTextColor mt-0.5">
                Your work summary for today
              </p>
            </div>

            {/* Right - Optional Stats Badge */}
            {!loading && data && (
              <div className="flex items-center gap-2">
                <div className="px-3 py-1.5 rounded-full bg-white shadow-md border border-gray-200">
                  <div className="flex items-center gap-1.5 text-xs font-medium">
                    <Briefcase size={14} className="text-primaryColor" />
                    <span className="text-primaryTextColor">{data.assignments.today}</span>
                    <span className="hidden sm:inline text-gray-500">jobs</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            icon={Briefcase}
            label="Assigned Today"
            value={assignments.today}
          />
          <StatCard
            icon={Calendar}
            label="Ongoing Jobs"
            value={actions.needsCompletion}
          />
          <StatCard
            icon={CheckCircle}
            label="Completed Today"
            value={schedule.completed}
          />
          <StatCard
            icon={Package}
            label="Product Requests"
            value={products.totalRequests}
          />

          <StatCard
            icon={Package}
            label="Pending Requests"
            value={products.pendingRequests}
          />

        </div>

        {/* Attendance */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-primaryTextColor">
            Attendance
          </h2>

          <StaffAttendanceCalendar
            workingHours={workingHours}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
          />
        </div>


        {/* Today Jobs */}
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-primaryTextColor">
            Today’s Jobs
          </h2>

          {assignments.ongoing && (
            <JobCard
              title="Ongoing Job"
              slot={assignments.ongoing.slot}
              location={assignments.ongoing.location}
            />
          )}

          {assignments.next && (
            <JobCard
              title="Next Job"
              slot={assignments.next.slot}
              location="Upcoming"
            />
          )}

          {!assignments.ongoing && !assignments.next && (
            <p className="text-sm text-secondaryTextColor">
              No jobs scheduled for today 🎉
            </p>
          )}
        </div>

        {/* Product Summary */}
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-primaryTextColor mb-2">
            Product Summary
          </h2>

          <div className="flex justify-between text-sm text-secondaryTextColor">
            <span>Total Requests</span>
            <span className="font-medium">{products.totalRequests}</span>
          </div>

          <div className="flex justify-between text-sm text-secondaryTextColor">
            <span>Pending Requests</span>
            <span className="font-medium text-orange-600">
              {products.pendingRequests}
            </span>
          </div>

          <div className="flex justify-between text-sm text-secondaryTextColor">
            <span>Approved Quantity</span>
            <span className="font-medium text-green-600">
              {products.approvedQuantity}
            </span>
          </div>

          <div className="flex justify-between text-sm text-secondaryTextColor">
            <span>Pending Quantity</span>
            <span className="font-medium text-red-600">
              {products.pendingQuantity}
            </span>
          </div>

        </div>
      </div>
    </div>
  );
};

const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

const StaffDashboardSkeleton = () => {
  return (
    <div className="p-4 space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>

      {/* Jobs */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-32" />
        {[...Array(2)].map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>

      {/* Product summary */}
      <Skeleton className="h-32" />
    </div>
  );
};

export default StaffDashboard;

// import { Briefcase, Calendar, CheckCircle, Package } from "lucide-react";
// import { useEffect } from "react";
// const staffDashboardMock = {
//   stats: {
//     assignedToday: 5,
//     ongoing: 2,
//     completedToday: 3,
//     pendingProducts: 1,
//   },

//   todayJobs: [
//     {
//       _id: "job1",
//       customerName: "Ravi Kumar",
//       phoneNumber: "9876543210",
//       status: "ongoing",
//       slotSnapshot: {
//         startTime: "10:00 AM",
//         endTime: "11:00 AM",
//         slotName: "Morning Slot",
//       },
//       location: {
//         address: {
//           locality: "Banjara Hills",
//           city: "Hyderabad",
//         },
//       },
//       payment: {
//         method: "PAY_AFTER_SERVICE",
//         status: "unpaid",
//       },
//     },
//     {
//       _id: "job2",
//       customerName: "Anita Sharma",
//       phoneNumber: "9123456789",
//       status: "assigned",
//       slotSnapshot: {
//         startTime: "2:00 PM",
//         endTime: "3:00 PM",
//         slotName: "Afternoon Slot",
//       },
//       location: {
//         address: {
//           locality: "Whitefield",
//           city: "Bangalore",
//         },
//       },
//       payment: {
//         method: "UPI_QR",
//         status: "paid",
//       },
//     },
//   ],

//   productSummary: {
//     assigned: 6,
//     used: 3,
//     requestedPending: 2,
//   },
// };

// const StatCard = ({ icon: Icon, label, value }) => (
//   <div className="bg-white rounded-xl border p-4 flex items-center gap-4 shadow-sm">
//     <div className="w-10 h-10 rounded-full bg-brandSurface flex items-center justify-center">
//       <Icon size={18} className="text-accentTextColor" />
//     </div>
//     <div>
//       <p className="text-xs text-secondaryTextColor">{label}</p>
//       <p className="text-lg font-semibold text-primaryTextColor">{value}</p>
//     </div>
//   </div>
// );

// const JobCard = ({ job }) => {
//   const statusMap = {
//     assigned: "bg-yellow-100 text-yellow-700",
//     ongoing: "bg-blue-100 text-blue-700",
//     completed: "bg-green-100 text-green-700",
//   };

//   return (
//     <div className="bg-white rounded-xl border p-4 shadow-sm space-y-2">
//       <div className="flex justify-between items-start">
//         <div>
//           <h3 className="font-semibold text-primaryTextColor">
//             {job.customerName}
//           </h3>
//           <p className="text-xs text-secondaryTextColor">
//             {job.location.address.locality}, {job.location.address.city}
//           </p>
//         </div>

//         <span
//           className={`text-xs px-3 py-1 rounded-full font-medium ${
//             statusMap[job.status]
//           }`}
//         >
//           {job.status}
//         </span>
//       </div>

//       <div className="text-xs text-secondaryTextColor flex justify-between">
//         <span>
//           {job.slotSnapshot.startTime} – {job.slotSnapshot.endTime}
//         </span>
//         <span>{job.payment.method}</span>
//       </div>
//     </div>
//   );
// };

// const StaffDashboard = () => {
//   const { stats, todayJobs, productSummary } = staffDashboardMock;

//   useEffect(() => {
//     window.scrollTo({ top: 0, behavior: "smooth" });
//   }, []);

//   return (
//     <div className="min-h-screen bg-gray-50 ">
//       <div className="sticky top-0 z-10 bg-primaryColor/10 backdrop-blur border-b border-primaryColor/10">
//         <div className="px-6 flex  py-2 lg:py-4">
//           <div className="flex items-center justify-between">
//             {/* Left */}
//             <div className="leading-tight">
//               <h1 className="text-lg md:text-xl font-semibold text-primaryTextColor">
//                 Dashboard
//               </h1>
//               <p className="text-[0.7em] sm:text-xs text-secondaryTextColor">
//                 Your work summary for today
//               </p>
//             </div>
//           </div>
//         </div>
//       </div>
//       <div className="p-4  space-y-5">
//         {/* Header */}

//         {/* Stats */}
//         <div className="grid grid-cols-2 gap-3">
//           <StatCard
//             icon={Briefcase}
//             label="Assigned Today"
//             value={stats.assignedToday}
//           />
//           <StatCard
//             icon={Calendar}
//             label="Ongoing Jobs"
//             value={stats.ongoing}
//           />
//           <StatCard
//             icon={CheckCircle}
//             label="Completed Today"
//             value={stats.completedToday}
//           />
//           <StatCard
//             icon={Package}
//             label="Pending Products"
//             value={stats.pendingProducts}
//           />
//         </div>

//         {/* Today Jobs */}
//         <div className="space-y-3">
//           <h2 className="text-sm font-semibold text-primaryTextColor">
//             Today’s Jobs
//           </h2>

//           {todayJobs.map((job) => (
//             <JobCard key={job._id} job={job} />
//           ))}
//         </div>
//         {/* Product Summary */}
//         <div className="bg-white rounded-xl border p-4 shadow-sm">
//           <h2 className="text-sm font-semibold text-primaryTextColor mb-2">
//             Product Summary
//           </h2>

//           <div className="flex justify-between text-sm text-secondaryTextColor">
//             <span>Assigned</span>
//             <span>{productSummary.assigned}</span>
//           </div>
//           <div className="flex justify-between text-sm text-secondaryTextColor">
//             <span>Used</span>
//             <span>{productSummary.used}</span>
//           </div>
//           <div className="flex justify-between text-sm text-secondaryTextColor">
//             <span>Pending Requests</span>
//             <span>{productSummary.requestedPending}</span>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default StaffDashboard;
